import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowRight, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { mockBlogs } from '../data/mock';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Property Management', 'Strata Management', 'Industry Insights', 'Career Tips'];

  const filteredBlogs = mockBlogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredBlogs = mockBlogs.filter(blog => blog.featured);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-50 via-white to-brand-50/50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-6 px-6 py-3 bg-brand-400/10 rounded-full border border-brand-400/20">
            <p className="text-brand-400 font-bold text-lg">📝 Insights & Resources</p>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
            Insights & <span className="text-brand-400">Resources</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-16 leading-relaxed">
            Expert advice, industry insights, and career tips from our recruitment specialists
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600" size={20} />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-white border-gray-300 text-gray-900 py-6 text-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-bold text-base transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-brand-400 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                }`}
              >
                {category === 'all' ? 'All Articles' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      {selectedCategory === 'all' && !searchTerm && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-black text-gray-900 mb-12 tracking-tight">Featured Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {featuredBlogs.map((blog) => (
                <Card key={blog.id} className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-2xl transition-all duration-300 group overflow-hidden hover:-translate-y-2">
                  <Link to={`/blog/${blog.id}`} className="block">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={blog.image} 
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-8">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-xs bg-brand-400 text-white px-3 py-1.5 rounded-full font-bold">
                          {blog.category}
                        </span>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock size={16} className="mr-1" />
                          {blog.readTime}
                        </div>
                      </div>
                      <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-brand-400 transition-colors duration-200 leading-tight">
                        {blog.title}
                      </h3>
                      <p className="text-gray-600 mb-6 text-base leading-relaxed">{blog.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <User size={16} className="mr-1 text-brand-400" />
                            {blog.author}
                          </div>
                          <div className="flex items-center">
                            <Calendar size={16} className="mr-1 text-brand-400" />
                            {new Date(blog.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        <Button variant="ghost" className="text-brand-400 hover:text-brand-500 hover:bg-brand-50 font-bold">
                          Read More
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </div>
                    </CardContent>
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Articles */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-black text-gray-900 mb-12 tracking-tight">
            {selectedCategory === 'all' ? 'Latest Articles' : selectedCategory}
          </h2>
          
          {filteredBlogs.length === 0 ? (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-12 text-center">
                <p className="text-gray-600 text-lg">No articles found matching your criteria.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <Card key={blog.id} className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 group overflow-hidden hover:-translate-y-2">
                  <Link to={`/blog/${blog.id}`} className="block h-full">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={blog.image} 
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-8">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-xs bg-brand-50 text-brand-400 px-3 py-1.5 rounded-full font-semibold">
                          {blog.category}
                        </span>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock size={14} className="mr-1" />
                          {blog.readTime}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-400 transition-colors duration-200 leading-tight line-clamp-2">
                        {blog.title}
                      </h3>
                      <p className="text-gray-600 mb-6 text-sm leading-relaxed line-clamp-3">{blog.excerpt}</p>
                      <div className="flex items-center text-sm text-gray-600 mb-4">
                        <User size={14} className="mr-1 text-brand-400" />
                        <span className="mr-3">{blog.author}</span>
                        <Calendar size={14} className="mr-1 text-brand-400" />
                        {new Date(blog.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                      </div>
                      <Button variant="ghost" className="text-brand-400 hover:text-brand-500 hover:bg-brand-50 font-bold p-0">
                        Read More
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </CardContent>
                  </Link>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-brand-50 via-white to-brand-50/50 border-brand-200 shadow-xl">
            <CardContent className="p-12 md:p-16 text-center">
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
                Never Miss an Update
              </h2>
              <p className="text-gray-700 mb-10 text-xl leading-relaxed">
                Subscribe to receive our latest articles, industry insights, and career tips
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 py-6 text-lg shadow-md"
                />
                <Button className="bg-brand-400 hover:bg-brand-500 text-white font-bold whitespace-nowrap px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  Subscribe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Blog;
